import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sources as api } from '../services/api';
import './Sources.css';

// ─── Console A97 DATA ────────────────────────────────────────────────────────
// Refonte du 2026-10-07. L'ancien écran unique mêlait trois usages de même
// poids : le contrôle quotidien (« les données de cette nuit sont-elles
// arrivées ? »), le projet de reprise CRM → entrepôt, et la fiche technique de
// chaque fichier. L'accueil répond désormais d'abord à la première question,
// liste ensuite ce qui demande une action, puis les sources — une ligne par
// source, ses périmètres (Antilles, Métropole, Groupe) regroupés. La reprise a
// son onglet ; la fiche technique vit dans le panneau.

// Étapes de la reprise, en mots d'utilisateur (codes inchangés en base).
const ETAPES = [
  ['inventoriee', 'Repérée'],
  ['ingeree', 'Importée en parallèle'],
  ['verifiee', 'Chiffres vérifiés'],
  ['basculee', 'Reprise'],
];
const RANG = { inventoriee: 0, ingeree: 1, verifiee: 2, basculee: 3, hors_perimetre: -1 };
const LIBELLE_ETAPE = Object.fromEntries(ETAPES);
const COPIES = [['crm', 'Le CRM'], ['data', "L'entrepôt"], ['aucun', 'Personne']];
const ORDRE_PERIMETRE = { Antilles: 0, 'Métropole': 1, Groupe: 2 };

// Heures affichées en heure des Antilles, là où travaillent les utilisateurs.
// L'heure de passage, elle, est stockée et comparée dans le fuseau du serveur
// (UTC, cf. l'ordonnanceur de server.js) : la Martinique est à UTC−4, sans
// heure d'été. À revoir si le serveur change de fuseau.
const FUSEAU = 'America/Martinique';
const DECALAGE_SERVEUR_H = 4;
const decaler = (hhmm, h) => {
  const [hh, mm] = String(hhmm || '23:30').split(':').map(Number);
  return `${String((hh + h + 24) % 24).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
const versAntilles = (hhmm) => decaler(hhmm, -DECALAGE_SERVEUR_H);
const versServeur = (hhmm) => decaler(hhmm, DECALAGE_SERVEUR_H);
const heureLisible = (hhmm) => hhmm.replace(':', ' h ');

const heuresDepuis = (d) => (d ? (Date.now() - new Date(d).getTime()) / 3600000 : null);
function ilYA(d) {
  const h = heuresDepuis(d);
  if (h == null) return '—';
  if (h < 1) return `il y a ${Math.max(1, Math.round(h * 60))} min`;
  if (h < 36) return `il y a ${Math.round(h)} h`;
  return `il y a ${Math.round(h / 24)} j`;
}
const jourHeure = (d) => new Date(d).toLocaleString('fr-FR', { timeZone: FUSEAU, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const heure = (d) => new Date(d).toLocaleTimeString('fr-FR', { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit' });
function nbLignes(n) {
  if (n == null) return '';
  if (n >= 1e6) return `${(n / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M lignes`;
  if (n >= 1e3) return `${Math.round(n / 1e3).toLocaleString('fr-FR')} k lignes`;
  return `${n} lignes`;
}
function octets(n) {
  if (!n) return '—';
  const mo = Number(n) / 1048576;
  if (mo >= 1024) return `${(mo / 1024).toFixed(1)} Go`;
  return mo >= 10 ? `${Math.round(mo)} Mo` : `${mo.toFixed(1)} Mo`;
}

// Santé d'une source : un import automatique de plus de 30 h est en retard
// (le passage est quotidien) ; un import manuel n'a pas de cadence.
function sante(s) {
  if (s.derniere_ingestion_statut === 'echec') return 'echec';
  if (!s.derniere_ingestion_le) return 'jamais';
  if (s.planification_active && heuresDepuis(s.derniere_ingestion_le) > 30) return 'retard';
  return 'ok';
}

function enrichir(s) {
  const chemin = s.chemin || '';
  return {
    ...s,
    perimetre: chemin.startsWith('/SELLENS') ? 'Métropole' : chemin.startsWith('/GROUPE') ? 'Groupe' : 'Antilles',
    base: s.nom.replace(/ — (Auto Métropole|Groupe)$/, ''),
    sante: sante(s),
  };
}

function grouper(liste) {
  const parCle = {};
  for (const s of liste) {
    const cle = `${s.domaine}|${s.base}`;
    (parCle[cle] ||= { cle, domaine: s.domaine, nom: s.base, membres: [] }).membres.push(s);
  }
  return Object.values(parCle).map((g) => {
    g.membres.sort((a, b) => ORDRE_PERIMETRE[a.perimetre] - ORDRE_PERIMETRE[b.perimetre]);
    const importes = g.membres.filter((m) => m.derniere_ingestion_le)
      .sort((a, b) => new Date(b.derniere_ingestion_le) - new Date(a.derniere_ingestion_le));
    return {
      ...g,
      importee: importes.length > 0,
      auto: g.membres.find((m) => m.planification_active) || null,
      pii: g.membres.some((m) => m.pii),
      dernier: importes[0]?.derniere_ingestion_le || null,
      lignes: importes.reduce((t, m) => t + (m.derniere_ingestion_lignes || 0), 0),
    };
  }).sort((a, b) => a.domaine.localeCompare(b.domaine) || a.nom.localeCompare(b.nom));
}

function Sources({ user, onglet }) {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [scanEnCours, setScanEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [selection, setSelection] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const admin = user?.role === 'admin';

  useEffect(() => {
    api.liste()
      .then((res) => setDonnees(res.data))
      .catch((e) => setErreur(e.response?.data?.error || 'Catalogue indisponible'))
      .finally(() => setChargement(false));
  }, []);

  // « Tout réimporter » : l'état vient du serveur, interrogé toutes les 5 s
  // tant qu'une réingestion tourne ; à la fin, le catalogue est rechargé.
  const [reing, setReing] = useState(null);
  useEffect(() => { api.reingestion().then((r) => setReing(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!reing?.enCours) return undefined;
    const t = setInterval(() => {
      api.reingestion().then((r) => {
        setReing(r.data);
        if (!r.data.enCours) api.liste().then((x) => setDonnees(x.data)).catch(() => {});
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [reing?.enCours]);

  const appeler = useCallback(async (promesse, message) => {
    setEnregistrement(true);
    setErreur('');
    try {
      const res = await promesse();
      setDonnees(res.data);
    } catch (e) {
      setErreur(e.response?.data?.error || message);
    } finally {
      setEnregistrement(false);
    }
  }, []);

  const reimporterTout = async () => {
    if (!window.confirm("Réimporter maintenant toutes les sources automatiques, comme le passage du soir ?\n\nCompter une dizaine de minutes. Les rapports BI se mettent à jour d'eux-mêmes à la fin.")) return;
    setErreur('');
    try {
      const res = await api.reingererTout();
      setReing(res.data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Réimport impossible');
    }
  };
  const verifierDepot = async () => {
    setScanEnCours(true);
    await appeler(() => api.scanner(), 'Vérification du dépôt impossible');
    setScanEnCours(false);
  };
  const actions = {
    modifier: (code, champs) => appeler(() => api.modifier(code, champs), 'Modification impossible'),
    importer: (code) => appeler(() => api.ingerer(code), 'Import impossible'),
    schemaVu: (code) => appeler(() => api.schemaVu(code), 'Impossible de marquer le changement comme vu'),
  };

  const liste = useMemo(() => (donnees?.sources || []).map(enrichir), [donnees]);
  const groupes = useMemo(() => grouper(liste), [liste]);
  const ouvrirSource = (code) => {
    const s = liste.find((x) => x.code === code);
    if (s) setSelection(`${s.domaine}|${s.base}`);
  };
  const groupeOuvert = groupes.find((g) => g.cle === selection) || null;

  if (chargement) return <div className="da-page da-vide">Chargement…</div>;
  if (!donnees) return <div className="da-page"><div className="da-alerte da-alerte--rouge">{erreur}</div></div>;

  return (
    <div className="da-page">
      {erreur && <div className="da-alerte da-alerte--rouge" role="alert">{erreur}</div>}
      {!donnees.sftp_configure && (
        <div className="da-alerte da-alerte--rouge">Connecteur SFTP non configuré sur ce serveur : le dépôt ne peut pas être vérifié.</div>
      )}

      {onglet === 'reprise' ? (
        <Reprise liste={liste} onOuvrir={ouvrirSource} />
      ) : (
        <>
          <EtatDuJour liste={liste} passages={donnees.passages || []} reing={reing} admin={admin}
            scanEnCours={scanEnCours} dernierScan={donnees.dernier_scan}
            onVerifier={verifierDepot} onReimporter={reimporterTout} />
          <ATraiter liste={liste} nonDeclares={donnees.non_declares} onOuvrir={ouvrirSource} />
          <TableSources groupes={groupes} onOuvrir={setSelection} />
        </>
      )}

      {groupeOuvert && (
        <Panneau groupe={groupeOuvert} admin={admin} enregistrement={enregistrement}
          actions={actions} onFermer={() => setSelection(null)} />
      )}
    </div>
  );
}

// ─── 1. L'état du jour ───────────────────────────────────────────────────────
function EtatDuJour({ liste, passages, reing, admin, scanEnCours, dernierScan, onVerifier, onReimporter }) {
  const auto = liste.filter((s) => s.planification_active);
  const aSuivre = auto.filter((s) => s.sante !== 'ok');
  const ok = aSuivre.length === 0;
  const dernier = passages[passages.length - 1];

  const enCours = reing?.sources?.length > 0 && (reing.enCours || reing.fin);
  const faites = enCours ? reing.sources.filter((x) => x.statut === 'succes' || x.statut === 'echec').length : 0;
  const echecsReing = enCours ? reing.sources.filter((x) => x.statut === 'echec') : [];

  return (
    <section className="da-etat" aria-live="polite">
      <div className={`da-etat-icone ${ok ? '' : 'da-etat-icone--ko'}`} aria-hidden="true">
        {ok ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 7v6M12 17h.01" /></svg>
        )}
      </div>
      <div className="da-etat-texte">
        <h1>{ok ? 'Les données sont à jour' : `${aSuivre.length} source${aSuivre.length > 1 ? 's' : ''} automatique${aSuivre.length > 1 ? 's' : ''} non importée${aSuivre.length > 1 ? 's' : ''}`}</h1>
        <p>
          {auto.length - aSuivre.length} sur {auto.length} sources importées automatiquement
          {dernier && <> · dernier passage {jourHeure(dernier.debut)}{dernier.fin && <> → {heure(dernier.fin)}</>} (heure des Antilles)</>}
          {' '}· rapports BI recalculés dans la foulée
        </p>
        {enCours && (
          <div className={`da-reing ${reing.enCours ? '' : echecsReing.length ? 'da-reing--ko' : 'da-reing--ok'}`}>
            {reing.enCours
              ? <>Réimport en cours : <b>{faites} / {reing.sources.length}</b> sources</>
              : <>Réimport terminé {ilYA(reing.fin)} : <b>{faites - echecsReing.length} / {reing.sources.length}</b> sources importées{echecsReing.length > 0 && <> · échec : {echecsReing.map((x) => x.code).join(', ')}</>}</>}
            <div className="da-reing-jauge"><span style={{ width: `${Math.round((faites / reing.sources.length) * 100)}%` }} /></div>
          </div>
        )}
      </div>
      <div className="da-etat-droite">
        {passages.length > 0 && (
          <>
            <div className="da-nuits" role="img"
              aria-label={`Passages automatiques des ${passages.length} derniers jours : ${passages.filter((p) => p.echecs).length} avec échec`}>
              {passages.map((p) => (
                <i key={p.jour} className={p.echecs ? 'da-nuit--ko' : ''}
                  title={`${new Date(p.debut).toLocaleDateString('fr-FR', { timeZone: FUSEAU, day: 'numeric', month: 'short' })} : ${p.reussis} sources importées, ${p.echecs} échec`} />
              ))}
            </div>
            <small>{passages.length} derniers passages · {passages.reduce((t, p) => t + p.echecs, 0)} échec</small>
          </>
        )}
        {admin && (
          <div className="da-boutons">
            <button className="da-bouton da-bouton--discret" onClick={onVerifier} disabled={scanEnCours}
              title={`Regarde quels fichiers sont sur le dépôt et de quand ils datent, sans rien importer.${dernierScan ? ` Dernière vérification ${ilYA(dernierScan.lance_le)}.` : ''}`}>
              {scanEnCours ? 'Vérification…' : 'Vérifier le dépôt'}
            </button>
            <button className="da-bouton" onClick={onReimporter} disabled={reing?.enCours || reing?.occupe}
              title="Importe tout de suite toutes les sources automatiques, comme le passage du soir.">
              {reing?.enCours ? 'Réimport en cours…' : 'Tout réimporter'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── 2. À traiter ────────────────────────────────────────────────────────────
function ATraiter({ liste, nonDeclares, onOuvrir }) {
  const echecs = liste.filter((s) => s.sante === 'echec');
  const retards = liste.filter((s) => s.sante === 'retard');
  const modifies = liste.filter((s) => s.schema_modifie);
  const attendues = liste.filter((s) => s.connecteur === 'sftp' && s.present === false
    && s.maitre === 'data' && s.etat_bascule !== 'hors_perimetre');
  const sellens = nonDeclares.filter((f) => f.chemin.startsWith('/SELLENS')).length;

  const cartes = [
    echecs.length > 0 && {
      niveau: 'rouge', titre: `${echecs.length} import${echecs.length > 1 ? 's' : ''} en échec`,
      texte: 'Les rapports BI montrent encore les données de la veille pour ces sources. Ouvrez-en une pour relancer son import.',
      sources: echecs,
    },
    retards.length > 0 && {
      niveau: 'rouge', titre: `${retards.length} source${retards.length > 1 ? 's' : ''} automatique${retards.length > 1 ? 's' : ''} sans import depuis plus de 30 h`,
      texte: `Le passage du soir ne ${retards.length > 1 ? 'les a' : "l'a"} pas ${retards.length > 1 ? 'importées' : 'importée'}. Vérifiez que le serveur a bien tourné, puis relancez l'import.`,
      sources: retards,
    },
    modifies.length > 0 && {
      niveau: 'ambre', titre: `L'en-tête de ${modifies.length} fichier${modifies.length > 1 ? 's' : ''} a changé`,
      texte: 'Une colonne ajoutée, retirée ou renommée peut fausser un rapport. Ouvrez la source pour comparer les colonnes, puis marquez le changement comme vu.',
      sources: modifies,
    },
    nonDeclares.length > 0 && {
      niveau: 'bleu', titre: `${nonDeclares.length} fichier${nonDeclares.length > 1 ? 's' : ''} du dépôt ne ${nonDeclares.length > 1 ? 'sont rattachés' : 'est rattaché'} à aucune source`,
      texte: `L'entrepôt ne les lit pas${sellens ? ` ; ${sellens} viennent de Sellens` : ''}. Pour en importer un, il faut le déclarer comme source (demande à l'équipe data).`,
      fichiers: nonDeclares.map((f) => f.chemin),
    },
    attendues.length > 0 && {
      niveau: 'bleu', titre: `${attendues.length} source${attendues.length > 1 ? 's' : ''} attendue${attendues.length > 1 ? 's' : ''} sans fichier sur le dépôt`,
      texte: "Déclarées dans l'entrepôt, mais le fichier n'est pas sur le dépôt. Rien à faire ici tant que l'export n'est pas déposé.",
      sources: attendues,
    },
  ].filter(Boolean);

  return (
    <section className="da-section" aria-labelledby="da-t-traiter">
      <div className="da-section-tete">
        <h2 id="da-t-traiter">À traiter</h2>
        <span>{cartes.length ? `${cartes.length} point${cartes.length > 1 ? 's' : ''}` : 'Rien à traiter'}</span>
      </div>
      {cartes.length === 0 && (
        <p className="da-aide">Aucun point en attente : les imports sont passés et le dépôt ne contient rien de nouveau.</p>
      )}
      {cartes.map((c) => (
        <article key={c.titre} className={`da-carte da-carte--${c.niveau}`}>
          <h3>{c.titre}</h3>
          <p>{c.texte}</p>
          <details>
            <summary>Voir la liste ({(c.sources || c.fichiers).length})</summary>
            <ul>
              {c.sources && c.sources.map((s) => (
                <li key={s.code}><button type="button" className="da-lien" onClick={() => onOuvrir(s.code)}>{s.nom}</button></li>
              ))}
              {c.fichiers && c.fichiers.map((f) => <li key={f}><code>{f}</code></li>)}
            </ul>
          </details>
        </article>
      ))}
    </section>
  );
}

// ─── 3. Les sources ──────────────────────────────────────────────────────────
const CLASSE_SANTE = { ok: 'da-perim--ok', retard: 'da-perim--retard', echec: 'da-perim--echec', jamais: '' };
const TITRE_SANTE = { ok: 'import réussi', retard: 'import automatique en retard', echec: 'dernier import en échec', jamais: 'jamais importée' };

function TableSources({ groupes, onOuvrir }) {
  const [toutes, setToutes] = useState(false);
  const [recherche, setRecherche] = useState('');
  const q = recherche.trim().toLowerCase();
  const visibles = groupes.filter((g) => (toutes || g.importee)
    && (!q || [g.nom, ...g.membres.map((m) => m.chemin), ...g.membres.map((m) => m.consommateur_crm)]
      .some((v) => (v || '').toLowerCase().includes(q))));
  const nbImportees = groupes.filter((g) => g.importee).length;

  return (
    <section className="da-section" aria-labelledby="da-t-sources">
      <div className="da-section-tete da-section-tete--barre">
        <h2 id="da-t-sources">Sources</h2>
        <div className="da-outils">
          <input className="da-recherche" type="search" placeholder="Rechercher une source ou un fichier"
            aria-label="Rechercher une source" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          <div className="da-seg" role="group" aria-label="Sources affichées">
            <button type="button" aria-pressed={!toutes} onClick={() => setToutes(false)}>Importées ({nbImportees})</button>
            <button type="button" aria-pressed={toutes} onClick={() => setToutes(true)}>Toutes ({groupes.length})</button>
          </div>
        </div>
      </div>
      <div className="da-tableau-cadre">
        <table className="da-tableau">
          <thead>
            <tr><th>Source</th><th>Périmètres</th><th>Dernier import</th><th className="da-col-mode">Mode</th></tr>
          </thead>
          <tbody>
            {visibles.map((g, i) => (
              <React.Fragment key={g.cle}>
                {(i === 0 || visibles[i - 1].domaine !== g.domaine) && (
                  <tr className="da-groupe"><td colSpan={4}>{g.domaine}</td></tr>
                )}
                <tr className="da-ligne" tabIndex={0} onClick={() => onOuvrir(g.cle)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOuvrir(g.cle); }}>
                  <td>
                    <span className="da-source-nom">{g.nom}</span>
                    {g.pii && <span className="da-perso" title="Contient des données personnelles">données perso</span>}
                  </td>
                  <td>
                    <div className="da-perims">
                      {g.membres.map((m) => (
                        <span key={m.code} className={`da-perim ${CLASSE_SANTE[m.sante]}`} title={`${m.chemin} — ${TITRE_SANTE[m.sante]}`}>{m.perimetre}</span>
                      ))}
                    </div>
                  </td>
                  <td className="da-import">
                    {g.dernier ? <><b>{ilYA(g.dernier)}</b><small>{nbLignes(g.lignes)}</small></> : <span className="da-aide">Jamais importée</span>}
                  </td>
                  <td className="da-col-mode">
                    {g.auto ? (
                      <><span className="da-pastille da-pastille--auto">Automatique</span><small>chaque soir, {heureLisible(versAntilles(g.auto.planification_heure_txt))}</small></>
                    ) : g.importee ? (
                      <><span className="da-pastille da-pastille--manuel">Manuel</span><small>à la demande</small></>
                    ) : (
                      <><span className="da-pastille">Non importée</span><small>{g.membres.some((m) => m.maitre === 'crm') ? 'lue par le CRM' : 'sans usage'}</small></>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {visibles.length === 0 && <tr><td colSpan={4} className="da-vide">Aucune source ne correspond à cette recherche.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="da-aide">Une ligne par source, ses périmètres regroupés. La pastille de chaque périmètre est verte quand l'import a réussi, orange quand l'import automatique est en retard, rouge en cas d'échec. Cliquez une ligne pour le détail.</p>
    </section>
  );
}

// ─── Onglet Reprise CRM → entrepôt ───────────────────────────────────────────
function Reprise({ liste, onOuvrir }) {
  const hors = liste.filter((s) => s.etat_bascule === 'hors_perimetre');
  const reprises = liste.filter((s) => s.maitre === 'data' && s.etat_bascule !== 'hors_perimetre');
  const crm = liste.filter((s) => s.maitre === 'crm' && s.etat_bascule !== 'hors_perimetre')
    .sort((a, b) => (RANG[b.etat_bascule] ?? 0) - (RANG[a.etat_bascule] ?? 0));
  const sans = liste.filter((s) => s.maitre === 'aucun' && s.etat_bascule !== 'hors_perimetre');
  const total = liste.length || 1;
  const largeur = (n) => `${(n / total) * 100}%`;
  const Item = ({ s, detail }) => (
    <li><button type="button" className="da-lien" onClick={() => onOuvrir(s.code)}>{s.nom}</button> <small>· {detail}</small></li>
  );

  return (
    <>
      <section className="da-reprise">
        <p className="da-etiquette">Projet de reprise</p>
        <div><span className="da-reprise-chiffre">{reprises.length} sur {reprises.length + crm.length}</span> <span className="da-aide">sources utiles sont servies par l'entrepôt</span></div>
        <div className="da-jauge" role="img" aria-label={`${reprises.length} reprises, ${crm.length} encore lues par le CRM`}>
          <span className="da-jauge-part--data" style={{ width: largeur(reprises.length) }} />
          <span className="da-jauge-part--crm" style={{ width: largeur(crm.length) }} />
          <span className="da-jauge-part--dormante" style={{ width: largeur(sans.length) }} />
        </div>
        <div className="da-legende">
          <span><i className="da-puce da-puce--data" />Reprises par l'entrepôt <b>{reprises.length}</b></span>
          <span><i className="da-puce da-puce--crm" />Encore lues par le CRM <b>{crm.length}</b></span>
          <span><i className="da-puce da-puce--dormante" />Sans usage <b>{sans.length}</b></span>
          <span><i className="da-puce da-puce--hors" />Hors périmètre <b>{hors.length}</b></span>
        </div>
        <p className="da-aide">Objectif : que le CRM, BI et les autres outils lisent tous la même copie, celle de l'entrepôt. Une source passe par quatre étapes : repérée, importée en parallèle, chiffres vérifiés, reprise.</p>
      </section>
      <div className="da-colonnes3">
        <section className="da-liste">
          <h2>Encore lues par le CRM <small>({crm.length})</small></h2>
          <ul>{crm.map((s) => <Item key={s.code} s={s} detail={`${LIBELLE_ETAPE[s.etat_bascule] || '—'}${s.consommateur_crm ? ` · écran ${s.consommateur_crm}` : ''}`} />)}</ul>
        </section>
        <section className="da-liste">
          <h2>Reprises par l'entrepôt <small>({reprises.length})</small></h2>
          <ul>{reprises.map((s) => <Item key={s.code} s={s} detail={LIBELLE_ETAPE[s.etat_bascule] || '—'} />)}</ul>
        </section>
        <section className="da-liste">
          <h2>Sans usage ou hors périmètre <small>({sans.length + hors.length})</small></h2>
          <ul>{[...sans, ...hors].map((s) => <Item key={s.code} s={s} detail={s.etat_bascule === 'hors_perimetre' ? 'hors périmètre' : 'personne ne la lit'} />)}</ul>
        </section>
      </div>
    </>
  );
}

// ─── Panneau de détail ───────────────────────────────────────────────────────
function Panneau({ groupe, admin, enregistrement, actions, onFermer }) {
  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', echap);
    return () => document.removeEventListener('keydown', echap);
  }, [onFermer]);

  return (
    <>
      <div className="da-voile" onClick={onFermer} />
      <aside className="da-panneau" role="dialog" aria-modal="true" aria-labelledby="da-p-titre">
        <header className="da-panneau-entete">
          <div>
            <p className="da-etiquette">{groupe.domaine}</p>
            <h2 id="da-p-titre">{groupe.nom}</h2>
            {groupe.pii && <p className="da-aide">Contient des données personnelles : accès limité, jamais exposées telles quelles dans BI.</p>}
          </div>
          <button className="da-fermer" onClick={onFermer} aria-label="Fermer" autoFocus>×</button>
        </header>
        {groupe.membres.map((m) => (
          <Membre key={m.code} source={m} plusieurs={groupe.membres.length > 1}
            admin={admin} enregistrement={enregistrement} actions={actions} />
        ))}
      </aside>
    </>
  );
}

function Membre({ source: s, plusieurs, admin, enregistrement, actions }) {
  const [note, setNote] = useState(s.notes || '');
  useEffect(() => { setNote(s.notes || ''); }, [s.code, s.notes]);
  const ajoutees = s.schema_modifie ? s.colonnes.filter((c) => !s.colonnes_precedentes.includes(c)) : [];
  const retirees = s.schema_modifie ? s.colonnes_precedentes.filter((c) => !s.colonnes.includes(c)) : [];
  const rang = RANG[s.etat_bascule] ?? 0;
  const fichier = s.connecteur === 'sftp';

  return (
    <section className="da-membre">
      {plusieurs && <h3 className="da-membre-titre">{s.perimetre}</h3>}

      {s.schema_modifie && (
        <div className="da-alerte da-alerte--ambre">
          <b>L'en-tête du fichier a changé {ilYA(s.schema_modifie_le)}.</b>
          {ajoutees.length > 0 && <div>Colonnes ajoutées : {ajoutees.join(', ')}</div>}
          {retirees.length > 0 && <div>Colonnes retirées : {retirees.join(', ')}</div>}
          {!ajoutees.length && !retirees.length && <div>Mêmes colonnes, dans un autre ordre.</div>}
          {admin && <button className="da-bouton da-bouton--discret" disabled={enregistrement} onClick={() => actions.schemaVu(s.code)}>Marquer comme vu</button>}
        </div>
      )}

      <div className="da-bloc">
        <h4>Import</h4>
        <dl className="da-faits-liste">
          <div><dt>Dernier import</dt><dd className={s.sante === 'echec' ? 'da-ing-echec' : ''}>
            {s.derniere_ingestion_le
              ? `${jourHeure(s.derniere_ingestion_le)} · ${s.derniere_ingestion_statut === 'succes' ? 'réussi' : s.derniere_ingestion_statut === 'echec' ? 'en échec' : s.derniere_ingestion_statut}${s.derniere_ingestion_lignes != null ? ` · ${nbLignes(s.derniere_ingestion_lignes)}` : ''}`
              : "jamais : l'entrepôt ne lit pas encore cette source"}
          </dd></div>
          <div><dt>Mode</dt><dd>
            {s.planification_active ? `automatique, chaque soir à ${heureLisible(versAntilles(s.planification_heure_txt))} (heure des Antilles)` : s.derniere_ingestion_le ? 'manuel, à la demande' : 'non importée'}
          </dd></div>
        </dl>
        {admin && (
          <div className="da-planif">
            <label className="da-planif-bascule">
              <input type="checkbox" checked={s.planification_active === true} disabled={enregistrement}
                onChange={(e) => actions.modifier(s.code, { planification_active: e.target.checked })} />
              Importer chaque soir
            </label>
            <label className="da-planif-heure">
              à
              <input type="time" value={versAntilles(s.planification_heure_txt)}
                disabled={enregistrement || s.planification_active !== true}
                onChange={(e) => e.target.value && actions.modifier(s.code, { planification_heure: versServeur(e.target.value) })} />
            </label>
            <button className="da-bouton" disabled={enregistrement} onClick={() => actions.importer(s.code)}>
              {enregistrement ? 'Import en cours…' : 'Importer maintenant'}
            </button>
          </div>
        )}
      </div>

      <div className="da-bloc">
        <h4>{fichier ? 'Fichier' : 'Origine'}</h4>
        <dl className="da-faits-liste">
          <div><dt>{fichier ? 'Chemin' : 'Lu dans'}</dt><dd className="da-mono">{s.chemin}</dd></div>
          {fichier && (
            <div><dt>Déposé</dt><dd>
              {s.present === false ? 'absent du dépôt' : s.modifie_le
                ? `${ilYA(s.modifie_le)} · ${octets(s.taille_octets)}${s.nb_fichiers > 1 ? ` · ${s.nb_fichiers} fichiers` : ''}`
                : 'pas encore vérifié'}
            </dd></div>
          )}
          {s.nb_colonnes != null && (
            <div><dt>Colonnes</dt><dd>{s.nb_colonnes}{s.encodage ? ` · ${s.encodage}` : ''}{s.separateur ? ` · séparateur « ${s.separateur === '\t' ? 'TAB' : s.separateur} »` : ''}</dd></div>
          )}
          <div><dt>Système</dt><dd>{s.systeme_origine}</dd></div>
        </dl>
        {s.colonnes && (
          <details className="da-details">
            <summary>Voir les {s.colonnes.length} colonnes</summary>
            <div className="da-colonnes">
              {s.colonnes.map((c, i) => <span key={`${c}-${i}`} className="da-colonne">{c || <i>sans nom</i>}</span>)}
            </div>
          </details>
        )}
      </div>

      <div className="da-bloc">
        <h4>Reprise par l'entrepôt</h4>
        {s.consommateur_crm && <p className="da-aide">Lue aussi par le CRM : écran « {s.consommateur_crm} ».</p>}
        {s.etat_bascule === 'hors_perimetre' ? (
          <p className="da-aide">Hors périmètre : cette source ne sera pas reprise.</p>
        ) : (
          <ol className="da-etapes">
            {ETAPES.map(([code, libelle]) => (
              <li key={code}>
                <button type="button" className={`da-etape ${RANG[code] <= rang ? 'da-etape--faite' : ''}`}
                  disabled={!admin || enregistrement} onClick={() => actions.modifier(s.code, { etat_bascule: code })}>
                  {libelle}
                </button>
              </li>
            ))}
          </ol>
        )}
        {admin && (
          <div className="da-choix">
            <span className="da-choix-titre" title="Quelle copie les autres outils doivent lire. Une donnée, une seule copie de référence.">Copie de référence</span>
            {COPIES.map(([code, libelle]) => (
              <button key={code} className={`da-choix-bouton ${s.maitre === code ? 'da-choix-bouton--actif' : ''}`}
                disabled={enregistrement} onClick={() => actions.modifier(s.code, { maitre: code })}>{libelle}</button>
            ))}
            <button className={`da-choix-bouton ${s.etat_bascule === 'hors_perimetre' ? 'da-choix-bouton--actif' : ''}`}
              disabled={enregistrement}
              onClick={() => actions.modifier(s.code, { etat_bascule: s.etat_bascule === 'hors_perimetre' ? 'inventoriee' : 'hors_perimetre' })}>
              Hors périmètre
            </button>
          </div>
        )}
      </div>

      <div className="da-bloc">
        <h4>Note</h4>
        {admin ? (
          <>
            <textarea className="da-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              aria-label={`Note sur ${s.nom}`} />
            <button className="da-bouton da-bouton--discret" disabled={enregistrement || note === (s.notes || '')}
              onClick={() => actions.modifier(s.code, { notes: note })}>Enregistrer la note</button>
          </>
        ) : (
          <p className="da-aide">{s.notes || 'Aucune note.'}</p>
        )}
      </div>
    </section>
  );
}

export default Sources;
